import React, { useMemo } from "react";
import classnames from "classnames";
import moment from "moment";
import { useTable, Column } from "react-table";
import { HeaderBullet } from "@ugg/shared/components/common/HeaderBullet";

interface Invoice {
  currency?: string | null;
  paid?: boolean | null;
  brand?: string | null;
  last4?: string | null;
  periodStart?: number | null;
  periodEnd?: number | null;
  totalPaid?: number | null;
}

interface BillingHistoryProps {
  invoices: Invoice[];
}

interface TableRow {
  totalPaid: number | null | undefined;
  periodStart: number | null | undefined;
  currency: string | null | undefined;
  paymentMethod: {
    brand: string | null | undefined;
    last4: string | null | undefined;
  };
}

const BillingHistory: React.FC<BillingHistoryProps> = ({ invoices }) => {
  const data = invoices.map((payment) => {
    return {
      totalPaid: payment.totalPaid,
      periodStart: payment.periodStart,
      currency: payment.currency,
      paymentMethod: {
        brand: payment.brand,
        last4: payment.last4,
      },
    };
  });

  const columns = useMemo<Column<TableRow>[]>(
    () => [
      {
        Header: "Date",
        accessor: "periodStart",
        Cell: ({ value }: { value: number | null | undefined }) => (
          <div>{value ? moment(value * 1000).format("MMMM DD, YYYY") : "-"}</div>
        ),
      },
      {
        Header: "Description",
        Cell: () => <div>{"U.GG Boost"}</div>,
      },
      {
        Header: "Payment Method",
        accessor: "paymentMethod",
        Cell: ({ value }: { value: { brand: string | null | undefined; last4: string | null | undefined } }) => (
          <div>{value.brand === null && value.last4 === null ? "-" : `${value.brand}  x${value.last4}`}</div>
        ),
      },
      {
        Header: "Amount",
        accessor: "totalPaid",
        Cell: ({ value, row }: { value: number | null | undefined; row: { original: TableRow } }) => {
          if (value == null) return <div>-</div>;
          const currency = row.original.currency || "USD";
          const formatted = new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
          }).format(value);
          return <div>{formatted}</div>;
        },
      },
    ],
    [],
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data,
  });

  return (
    <div className="settings-section">
      <div className="billing-history">
        <div
          {...getTableProps({
            className: "content-section ReactTable ugg-table-2",
          })}
        >
          <HeaderBullet className="mt-[4px] mb-[24px]">Billing History</HeaderBullet>
          <div className={"rt-thead -header"}>
            {headerGroups.map((headerGroup, headerGroupIndex) => (
              <div {...headerGroup.getHeaderGroupProps({ className: "rt-tr" })} key={`header-group-${headerGroupIndex}`}>
                {headerGroup.headers.map((column, colIndex) => {
                  const thClassNames = classnames(["rt-th", column.headerClassName]);

                  return (
                    <div {...column.getHeaderProps({ className: thClassNames })} key={`header-${colIndex}`}>
                      {column.render("Header")}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
          <div {...getTableBodyProps({ className: "rt-tbody" })}>
            {rows.map((row, rowIndex) => {
              prepareRow(row);

              return (
                <div key={`row-group-${rowIndex}`} className="rt-tr-group">
                  <div {...row.getRowProps({ className: "rt-tr" })}>
                    {row.cells.map((cell, cellIndex) => {
                      const tdClassNames = classnames("rt-td", cell.column.className, {
                        "is-in-odd-row": rowIndex % 2 === 0,
                      });

                      return (
                        <div {...cell.getCellProps({ className: tdClassNames })} key={`cell-${rowIndex}-${cellIndex}`}>
                          {cell.render("Cell", { index: rowIndex })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingHistory;
